import { useMemo } from 'react';

import { matchSorter } from 'match-sorter';
import { Apollo } from 'src/gql/generated';

import type { ReminderListRow } from './ColumnDef';
import { UNASSIGNED_USER_ID } from './constants';
import type { RemindersLocalState } from './toolbar';

export function getIsPendingReview(
  r: Pick<Apollo.Reminder, 'completedAt' | 'reviewStatus'>
): boolean {
  return !r.completedAt && r.reviewStatus === Apollo.ReminderReviewStatus.PendingReview;
}

interface UseFilteredRemindersInput {
  reminders: ReminderListRow[];
  remindersState: RemindersLocalState;
}

interface UseFilteredRemindersResult {
  rows: ReminderListRow[];
}

export function useClientSideReminderFilters({
  reminders,
  remindersState,
}: UseFilteredRemindersInput): UseFilteredRemindersResult {
  const remindersFilteredByStatus = useMemo(() => {
    let filtered = [...reminders];

    if (remindersState.filterAutomationId) {
      filtered = filtered.filter(
        r => r.aiReminderAutomation?.id === remindersState.filterAutomationId
      );
    }

    if (remindersState.filterReportId) {
      filtered = filtered.filter(
        r => r.aiReminderAutomation?.report?.id === remindersState.filterReportId
      );
    }

    return filtered;
  }, [reminders, remindersState.filterAutomationId, remindersState.filterReportId]);

  const remindersFilteredByType = useMemo(() => {
    if (!remindersState.filterTypes.length) return remindersFilteredByStatus;

    return remindersFilteredByStatus.filter(reminder => {
      return remindersState.filterTypes.some(t => reminder.type === t);
    });
  }, [remindersFilteredByStatus, remindersState.filterTypes]);

  const remindersFilteredByAssignee = useMemo(() => {
    if (!remindersState.filterAssignees.length) return remindersFilteredByType;

    const filterIds = new Set(remindersState.filterAssignees.map(a => a.id));
    const isUnassignedChecked = filterIds.has(UNASSIGNED_USER_ID);
    return remindersFilteredByType.filter(reminder => {
      const hasMatchingAssignee = reminder.assignees.some(a => filterIds.has(a.id));
      if (!isUnassignedChecked) return hasMatchingAssignee;

      const isUnassigned = !reminder.assignees.length;
      return isUnassigned || hasMatchingAssignee;
    });
  }, [remindersFilteredByType, remindersState.filterAssignees]);

  const rows = useMemo(() => {
    if (!remindersState.searchString) return remindersFilteredByAssignee;

    const searchKeys: (keyof ReminderListRow)[] = ['title', 'type'];
    return matchSorter(remindersFilteredByAssignee, remindersState.searchString, {
      keys: searchKeys,
    });
  }, [remindersState.searchString, remindersFilteredByAssignee]);

  return { rows };
}
